import { __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { useRef, useState, useEffect } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { USE_HARDCODED_CONVERSATION, loadConversation } from '../api.js';
import { getHeadersWithUniqueVisitorId } from '../../Auth/index.js';
import { processConversationData } from '../utils/conversationProcessor.js';

const useConversationLoader = ({ conversationId, onConversationLoaded, onConversationNotFound, isActivelyStreaming = false, conversationHistoryLength = 0, apiBaseUrl, }) => {
    const queryClient = useQueryClient();
    const loadedConversationIdRef = useRef(null);
    const [forceReload, setForceReload] = useState(0);
    const previousConversationIdRef = useRef(null);
    const wasStreamingRef = useRef(false);
    // Track streaming state transitions
    useEffect(() => {
        wasStreamingRef.current = isActivelyStreaming;
    }, [isActivelyStreaming]);
    // Allow loading when conversationId changes (e.g., from sidebar selection)
    useEffect(() => {
        const prevId = previousConversationIdRef.current;
        previousConversationIdRef.current = conversationId;
        // Don't force reload if we're actively streaming
        if (isActivelyStreaming) {
            // Just mark as loaded to prevent future loads during this streaming session
            if (conversationId || USE_HARDCODED_CONVERSATION) {
                loadedConversationIdRef.current = conversationId || 'hardcoded';
            }
            return;
        }
        // If we just finished streaming, don't reload - we already have the data
        if (wasStreamingRef.current && !isActivelyStreaming) {
            if (conversationId || USE_HARDCODED_CONVERSATION) {
                loadedConversationIdRef.current = conversationId || 'hardcoded';
            }
            return;
        }
        if (conversationId && conversationId !== loadedConversationIdRef.current) {
            // Check if the conversation is already loaded in the context
            // This happens when navigating from widget to full page
            if (conversationHistoryLength > 0) {
                // Mark as loaded without triggering a query
                loadedConversationIdRef.current = conversationId;
                return;
            }
            // Force reload by incrementing state
            setForceReload((prev) => prev + 1);
            // Invalidate any cached conversation data to ensure fresh load
            queryClient.invalidateQueries({
                queryKey: ["conversation", conversationId],
            });
        }
        else if (conversationId &&
            prevId === null &&
            conversationId === loadedConversationIdRef.current) {
            // Special case: conversationId was null and now matches the already-loaded conversation
            // This happens when clicking the same conversation - force a reload
            setForceReload((prev) => prev + 1);
            queryClient.invalidateQueries({
                queryKey: ["conversation", conversationId],
            });
        }
    }, [conversationId, queryClient, isActivelyStreaming, conversationHistoryLength, forceReload]);
    // Use React Query to load conversation
    const { data: loadedConversationData, isLoading: isLoadingConversationQuery, error: conversationLoadError, } = useQuery({
        queryKey: ["conversation", conversationId, forceReload, USE_HARDCODED_CONVERSATION],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            // Otherwise, require conversationId
            if (!conversationId) {
                return null;
            }
            const headers = yield getHeadersWithUniqueVisitorId();
            return loadConversation({
                conversationId,
                headersToInject: headers,
                apiBaseUrl,
            });
        }),
        enabled: (!!conversationId) &&
            !isActivelyStreaming &&
            (conversationId !== loadedConversationIdRef.current),
        retry: false,
        staleTime: Infinity,
    });
    // Process loaded conversation data
    useEffect(() => {
        // For hardcoded conversation, process even if conversationId is null
        const shouldProcess = loadedConversationData && (conversationId);
        if (shouldProcess) {
            const effectiveConversationId = conversationId || (loadedConversationData === null || loadedConversationData === void 0 ? void 0 : loadedConversationData.conversationId) || 'hardcoded';
            // Check if user is unauthorized to view this conversation
            if (loadedConversationData.error === 'unauthorized') {
                console.warn(`Unauthorized to access conversation ${effectiveConversationId}, redirecting`);
                onConversationNotFound();
                loadedConversationIdRef.current = effectiveConversationId;
                // Optionally navigate to concierge home
                if (typeof window !== 'undefined') {
                    window.history.pushState({}, '', '/concierge');
                }
                return;
            }
            const { messages: processedMessages, productMap, followUpQuestions, previewSearchQueries, previewQueryProducts, } = processConversationData(loadedConversationData);
            if (processedMessages.length > 0) {
                onConversationLoaded(processedMessages, productMap, followUpQuestions, previewSearchQueries, previewQueryProducts);
                loadedConversationIdRef.current = effectiveConversationId;
            }
            else {
                // Conversation not found - notify parent
                console.warn(`Conversation ${effectiveConversationId} not found, clearing URL`);
                onConversationNotFound();
                loadedConversationIdRef.current = effectiveConversationId;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedConversationData, conversationId]);
    // Handle conversation load errors
    useEffect(() => {
        if (conversationLoadError && conversationId) {
            console.error("Failed to load conversation:", conversationLoadError);
            onConversationNotFound();
            loadedConversationIdRef.current = conversationId;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationLoadError, conversationId]);
    return {
        isLoadingConversationQuery,
        conversationLoadError,
    };
};

export { useConversationLoader };
//# sourceMappingURL=useConversationLoader.js.map
